import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import GameToolbar from "./GameToolbar";
import ModeChip from "./ModeChip";
import HelpModal from "../../../shared/help-modal/HelpModal";
import SettingsModal from "./SettingsModal";
import { useIsCompact } from "../../../shared/useResponsive";
import { HudBoardContext, ScoreFitContext, ScoreStripesContext } from "../../../shared/score-fit";
import { ArenaLiveContext, ArenaViewContext, useArenaRotated } from "../../../game-canvas/arena-view";
import { stripTiming } from "../round-over/RoundOverCard";
import styles from "../play.module.scss";

// CSS px per game unit a small map may scale up to before it stops spanning the
// full height and just centres instead (keeps worms from over-zooming).
const MAX_SCALE = 1.5;

// The wide layout's scoreboard height, last measured (see scoresH below).
let lastScoresH = 72;

// The shared desktop scaffold for the live game, the round-over screen and the
// replay. A full-viewport "stage": the arena is dead-centre and sizes to the
// viewport (full height, capped + centred on small maps so worms don't
// over-zoom, sized purely in CSS from the --ar / --maxw vars set below), with
// the info rail hugging its right edge. The rail has no panel of its own: the
// toolbar sits at its top corner, the score target headline under it and the
// scoreboard filling the rest, all straight on the stage background.
// Owns the toolbar's modals (help / settings) and the leave confirm so all three
// screens behave identically and don't drift apart.
interface Props {
  hud: React.ReactNode;
  effects?: React.ReactNode;
  arena: React.ReactNode;
  // optional row beneath the arena (replay uses it for the scrubber + pause)
  arenaFooter?: React.ReactNode;
  // How tall that footer is, so the arena leaves room for it (default: one row).
  arenaFooterHeight?: number;
  score: React.ReactNode;
  // the active map (game-state map), used to size the arena to its aspect ratio
  map: any;
  // the match's game mode (see presets matchPlaystyle), shown as a chip by the
  // toolbar so players can tell which mode they're in. Omitted = no chip.
  playstyle?: string | null;
  // a ranked match: a "Ranked" chip rides beside the mode chip
  ranked?: boolean;
  onLeave: () => void;
  // when false, the toolbar's leave fires onLeave straight away with no confirm
  // (a replay is harmless to abandon); defaults to the live game's confirm step.
  confirmLeave?: boolean;
  // the leave confirm's alert line (online: "Leaving the game counts as a loss!");
  // none for a local match.
  leaveNote?: string;
  // round-over uses this to pause its auto-advance while a modal is open
  onModalOpenChange?: (open: boolean) => void;
  // The round-over strip is showing along the arena's top edge. (The compact
  // panel stays up through it: the board settles its standings there like the
  // desktop rail does.)
  roundOver?: boolean;
  // The between-rounds wait (RoundOverCard's totalSeconds) and whether it is
  // paused: a long map on a phone slides its scoreboard out with the strip.
  roundOverTiming?: { totalSeconds: number; paused?: boolean };
  // local humans (for editing key bindings in settings)
  settingsPlayers?: any[];
  onChangeKey?: (playerId: string, side: "left" | "right", key: string) => void;
  // touch steering zones, layered over the whole stage on touch devices (both
  // layouts) - independent of the compact/roomy width split.
  touchControls?: React.ReactNode;
  // Hide the floating compact score (it sits where the turn zones are). Used for
  // 2-player same-device touch play so it doesn't block either side's steering.
  hideScoreCompact?: boolean;
  // Two players on one phone: each gets their own row (Score `only`) at their
  // end of the screen, player 2's turned to face them. Upright only (sideways
  // the board fills the height and the rotate hint is up).
  couchStrips?: { bottom: React.ReactNode; top: React.ReactNode };
  // Replace the whole info rail (toolbar + HUD + scores) with this node. The
  // shop's try-out match uses it for a lone "Leave preview" button.
  railOverride?: React.ReactNode;
  // Compact only: keep the toolbar (fullscreen / settings / help / leave) in
  // the screen's top right corner beside a railOverride. The ranked search's
  // practice round uses it.
  railTools?: boolean;
  // Is a round under way (its countdown ticking, or play)? Polled a few times
  // a second (a live round never re-renders React). On a phone where the arena
  // fills the screen, the HUD over it and the thumb prints fade while it is.
  getLive?: () => boolean;
}

export default function GameLayout({
  hud, effects, arena, arenaFooter, arenaFooterHeight = 64, score, map, playstyle, ranked, onLeave, confirmLeave = true, leaveNote,
  onModalOpenChange, settingsPlayers, onChangeKey, touchControls, hideScoreCompact, railOverride, railTools, roundOver, roundOverTiming, couchStrips, getLive,
}: Props) {
  const [help, setHelp] = useState(false);
  const [settings, setSettings] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const compact = useIsCompact();

  // The compact board panel's height, for Score's row budget (see the compact
  // branch below). Re-observed whenever the panel mounts or unmounts.
  const boardRef = useRef<HTMLDivElement>(null);
  const [boardH, setBoardH] = useState<number | null>(null);
  const boardMounted = compact && !railOverride && !hideScoreCompact;
  useEffect(() => {
    const el = boardRef.current;
    if (!boardMounted || !el || typeof ResizeObserver === "undefined") {
      setBoardH(null);
      return;
    }
    const ro = new ResizeObserver((entries) => {
      const h = Math.floor(entries[0].contentRect.height);
      setBoardH((prev) => (prev === h ? prev : h));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [boardMounted]);

  // The arena size is computed entirely in CSS from these two custom properties
  // (see .arena-col --arena-w): the map's aspect ratio and its MAX_SCALE-capped
  // pixel width. CSS then tracks the live viewport with no JS resize handling.
  // On an upright phone a landscape map is shown turned a quarter (arena-view.ts),
  // so the box takes the turned shape: the map's height across, its width down.
  const mapW = (map && map.width) || 1;
  const mapH = (map && map.height) || 1;
  const rotated = useArenaRotated(map, compact);
  const shownW = rotated ? mapH : mapW;
  const shownH = rotated ? mapW : mapH;
  // A long map (the 2:1 rectangle) fills a phone's screen, so there is no free
  // room around it for the HUD: it goes over the arena instead, and fades out
  // from the countdown's first number. Squares and circles keep their room around the board.
  const fill = compact && Math.abs(mapW / mapH - 1) > 0.2;
  // A wide map on a computer: the stripes layout (below).
  const wideDesktop = !compact && mapW / mapH > 1.2;
  // Its scoreboard's height, measured before the first paint (and remembered
  // across the round changes, which mount a fresh layout) so the board leaves
  // room for it without jumping, and so without the canvas repaint blinking.
  const scoresRef = useRef<HTMLDivElement>(null);
  const [scoresH, setScoresH] = useState(lastScoresH);
  useLayoutEffect(() => {
    const el = scoresRef.current;
    if (!wideDesktop || !el) return;
    const set = (h: number) => {
      if (h <= 0) return;
      lastScoresH = h;
      setScoresH((prev) => (prev === h ? prev : h));
    };
    set(Math.ceil(el.getBoundingClientRect().height));
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver((entries) => set(Math.ceil(entries[0].contentRect.height)));
    ro.observe(el);
    return () => ro.disconnect();
  }, [wideDesktop]);
  const [live, setLive] = useState(false);
  const getLiveRef = useRef(getLive);
  getLiveRef.current = getLive;
  const hasLive = !!getLive;
  const view = useMemo(() => ({ rotated }), [rotated]);
  useEffect(() => {
    if (!hasLive) {
      setLive(false);
      return;
    }
    const tick = () => setLive(!!getLiveRef.current?.());
    tick();
    const id = setInterval(tick, 200);
    return () => clearInterval(id);
  }, [hasLive]);
  const stageStyle = {
    "--ar": shownW / shownH,
    "--maxw": `${Math.round(shownW * MAX_SCALE)}px`,
    // Reserve bottom room for the arena footer (replay scrubber) in BOTH
    // layouts so the arena shrinks to fit and the scrubber isn't cut off at the
    // bottom of the viewport: 52px of controls + the 12px arena-col gap.
    "--footer-h": arenaFooter ? arenaFooterHeight + "px" : "0px",
  } as React.CSSProperties;

  const anyOpen = help || settings || confirming;
  // Only fire when the open/closed state actually flips. (onModalOpenChange is
  // often an inline arrow, so keeping it out of the deps avoids a render loop.)
  useEffect(() => {
    if (onModalOpenChange) onModalOpenChange(anyOpen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anyOpen]);

  const toolbar = (
    <GameToolbar
      onSettings={() => setSettings(true)}
      onHelp={() => setHelp(true)}
      onLeave={() => (confirmLeave ? setConfirming(true) : onLeave())}
    />
  );

  const modeChip = playstyle ? (
    <span className={styles.modeChips}>
      <ModeChip playstyle={playstyle} />
      {ranked && <span className={`${styles.modeChip} ${styles.modeChipRanked}`} title="A ranked match">Ranked</span>}
    </span>
  ) : null;

  const arenaBlock = (
    <div className={styles.arenaCol}>
      <div className={styles.localGameCanvasWrapper}>{arena}</div>
      {arenaFooter && <div className={styles.arenaFooter}>{arenaFooter}</div>}
    </div>
  );

  const modals = (
    <>
      {/* On the page's top level, like the dimmed Modal: the between-rounds
          screens (scores, the next round's card) would cover it otherwise. */}
      {confirming && createPortal(
        <div className="app-modal-backdrop" onClick={() => setConfirming(false)}>
          <div
            className={`app-modal ${styles.leaveConfirm}${leaveNote ? ` ${styles.leaveConfirmWarn}` : ""}`}
            role="dialog"
            aria-labelledby="leave-confirm-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className={`app-modal-head ${styles.leaveConfirmHead}`}>
              <h2 id="leave-confirm-title">Leave the match?</h2>
            </div>
            <div className="app-modal-body">
              {leaveNote ? (
                <>
                  <p className={styles.leaveAlert}>{leaveNote}</p>
                  <p className={styles.leaveDetail}>
                    You finish last, the others play on without you, and you can't rejoin this match.
                  </p>
                </>
              ) : (
                <p className={styles.leaveDetail}>You'll head back to the lobby and forfeit the rest of the match.</p>
              )}
            </div>
            <div className={`app-modal-foot ${styles.leaveConfirmFoot}`}>
              <button className={`app-btn app-btn-primary ${styles.leaveStay}`} autoFocus onClick={() => setConfirming(false)}>
                Keep playing
              </button>
              <button className={`app-btn ${styles.leaveGo}`} onClick={onLeave}>Leave match</button>
            </div>
          </div>
        </div>,
        document.body
      )}
      {settings && <SettingsModal onClose={() => setSettings(false)} players={settingsPlayers} onChangeKey={onChangeKey} />}
      {help && <HelpModal onClose={() => setHelp(false)} />}
    </>
  );

  // Compact (<= 1024px, reactive): the arena fills the viewport's width (upright)
  // or height (sideways), and the round readout, toolbar and scoreboard share
  // the room that leaves: a panel above the arena when upright, a rail beside
  // it when sideways (play.module.scss places it). The board gets whatever
  // height the panel has left under the readout row, measured here and handed
  // to Score, which fits as many rows as that holds (your own row always).
  if (compact) {
    // Fill mode's three states: between rounds the scoreboard shows over the
    // arena; through the roll call only the slim readout + toolbar row (the
    // spawns must stay visible); from the countdown on the readout fades and
    // the toolbar dims.
    const stageClass =
      styles.gameStageCompact +
      (fill ? ` ${styles.stageFill}` : "") +
      (fill && roundOver ? ` ${styles.stageOver}` : "") +
      (fill && live && !roundOver ? ` ${styles.stageLive}` : "") +
      (fill && roundOver && roundOverTiming?.paused ? ` ${styles.stagePaused}` : "");
    const compactStyle = fill && roundOver && roundOverTiming ? { ...stageStyle, ...stripTiming(roundOverTiming.totalSeconds) } : stageStyle;
    return (
      <ArenaViewContext.Provider value={view}>
      <ArenaLiveContext.Provider value={fill && live && !roundOver}>
      <div className={stageClass} style={compactStyle}>
        {arenaBlock}
        {railOverride ? (
          <>
            <div className={styles.compactRailBare}>{railOverride}</div>
            {railTools && <div className={styles.compactCornerTools}>{toolbar}</div>}
          </>
        ) : (
          <div className={couchStrips ? `${styles.phonePanel} ${styles.phonePanelCouch}` : styles.phonePanel}>
            <div className={styles.phonePanelTop}>
              {/* two players on one phone: the readout is in each player's
                  strip, so the mode chip takes its place beside the toolbar
                  (a line of its own would sit on player 2's thumb zone) */}
              <div className={styles.compactStack}>
                {couchStrips ? <span className={styles.compactModeCouch}>{modeChip}</span> : <div className={styles.compactHud}>{hud}</div>}
                {/* sideways: stacked in the left band under the readout
                    (upright it's at the screen's bottom, see below) */}
                {modeChip && !couchStrips && <div className={styles.compactMode}>{modeChip}</div>}
              </div>
              {/* the settings row: top right of the screen, always */}
              <div className={styles.compactToolbar}>{toolbar}</div>
            </div>
            {!hideScoreCompact && (
              <div ref={boardRef} className={styles.phoneBoard}>
                <ScoreFitContext.Provider value={boardH}>{score}</ScoreFitContext.Provider>
              </div>
            )}
          </div>
        )}
        {/* upright: the mode chip sits bottom centre of the screen, in the free
            room under the arena (between the thumb hints on touch), so it costs
            the scoreboard no line. Couch play keeps it by the toolbar: player
            1's strip is down there. */}
        {modeChip && !couchStrips && !railOverride && <div className={styles.compactModeBottom}>{modeChip}</div>}
        {/* each player's strip: the readout and their own row, the row nearest
            their edge (the top one is turned 180 degrees as a whole) */}
        {couchStrips && (
          <div className={styles.couchStripTop}>
            <div className={styles.compactHud}>{hud}</div>
            {couchStrips.top}
          </div>
        )}
        {couchStrips && (
          <div className={styles.couchStripBottom}>
            <div className={styles.compactHud}>{hud}</div>
            {couchStrips.bottom}
          </div>
        )}
        {touchControls}
        {modals}
      </div>
      </ArenaLiveContext.Provider>
      </ArenaViewContext.Provider>
    );
  }

  // Roomy, a wide map (the 2:1 rectangle or stadium): a side rail would leave
  // the board at half the screen, so there is none. A slim bar on top (the
  // readout, the mode chip, the toolbar), the board as wide as the window
  // allows, and the scoreboard as racing stripes under it (Score.tsx). The
  // square and the circle keep the rail below.
  if (wideDesktop) {
    const wideStyle = {
      ...stageStyle,
      "--scores-h": `${scoresH}px`,
      "--col-gaps": arenaFooter ? "24px" : "12px",
    } as React.CSSProperties;
    return (
      <ArenaViewContext.Provider value={view}>
      <ScoreStripesContext.Provider value={true}>
      <div className={styles.gameStageWide} style={wideStyle}>
        <div className={styles.wideTop}>
          {railOverride ? (
            <div />
          ) : (
            <div className={styles.wideTopLeft}>
              <div className={styles.wideHud}>{hud}</div>
              {modeChip}
            </div>
          )}
          <div className={styles.wideTopRight}>{railOverride ? railOverride : toolbar}</div>
        </div>
        <div className={styles.wideSlot}>
          <div className={styles.arenaCol}>
            <div className={styles.localGameCanvasWrapper}>{arena}</div>
            {arenaFooter && <div className={styles.arenaFooter}>{arenaFooter}</div>}
            {!railOverride && (
              <div ref={scoresRef} className={styles.wideScores}>
                {/* a duel: its big best-of marks take the rows' place (GameHud) */}
                <HudBoardContext.Provider value={true}>{hud}</HudBoardContext.Provider>
                <div className={styles.wideScoresRows}>{score}</div>
              </div>
            )}
          </div>
        </div>
        {touchControls}
        {modals}
      </div>
      </ScoreStripesContext.Provider>
      </ArenaViewContext.Provider>
    );
  }

  // Roomy (> 1024px): centred arena | info rail beside it (toolbar, headline, scoreboard).
  return (
    <ArenaViewContext.Provider value={view}>
    <div className={styles.gameStage} style={stageStyle}>
      {effects ? <div className={styles.effectsCol}>{effects}</div> : null}
      {arenaBlock}
      <div className={railOverride ? `${styles.gameInfoPanel} ${styles.gameInfoPanelBare}` : styles.gameInfoPanel}>
        {railOverride ? railOverride : (
          <>
            {/* the mode chip takes the free left end of the toolbar row */}
            <div className={styles.railTop}>
              {modeChip}
              {toolbar}
            </div>
            <div className={styles.gameInfoHud}>{hud}</div>
            <div className={styles.railScores}>{score}</div>
          </>
        )}
      </div>
      {touchControls}
      {modals}
    </div>
    </ArenaViewContext.Provider>
  );
}
